import { useState, useEffect, type ReactNode } from "react";
import { MdDownload, MdClose } from "react-icons/md";
import "@style/Admin.scss";
import Modal from "@modalSrc/Modal";
import DateTimePicker from "@elements/DateTimePicker";

export type DownloadDataFormat = "simple" | "v20";

export type DownloadDataOptions = {
  format: DownloadDataFormat;
  fromDate: string;
  toDate: string;
  completedOnly: boolean;
};

type DownloadDataModalProps = {
  isOpen: boolean;
  onDownload?: (options: DownloadDataOptions) => void;
  onClose: () => void;
  title?: string;
};

function toDateOnlyIso(d: Date): string {
  return d.toISOString().substring(0, 10);
}

export default function DownloadDataModal({
  isOpen,
  onDownload,
  onClose,
  title = "Download Data"
}: DownloadDataModalProps) {
  const [format, setFormat] = useState<DownloadDataFormat>("simple");
  const [selectedDate, setSelectedDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completedOnly, setCompletedOnly] = useState(true);

  useEffect(() => {
    if (isOpen) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setSelectedDate(toDateOnlyIso(start));
      setEndDate(toDateOnlyIso(end));
      setCompletedOnly(true);
      setFormat("simple");
    }
  }, [isOpen]);

  const rangeInvalid =
    !selectedDate || !endDate || selectedDate > endDate;

  const handleDownload = () => {
    if (onDownload && !rangeInvalid) {
      onDownload({
        format,
        fromDate: selectedDate,
        toDate: endDate,
        completedOnly,
      });
    }
  };

  const handleDateChange = (e: any) => {
    setSelectedDate(e?.target?.value || "");
  };

  const handleEndDateChange = (e: any) => {
    setEndDate(e?.target?.value || "");
  };

  if (!isOpen) {
    return null;
  }

  const modalTitle: ReactNode = (
    <>
      <MdDownload className="downloadDataModalTitleIcon" />
      {title}
    </>
  );

  const bodyContent = (
    <div className="downloadDataForm">
      <div className="form-group">
        <span className="form-label">Format</span>
        <div className="exportScopeToggle" role="radiogroup" aria-label="Export format">
          <label className={format === "simple" ? "active" : undefined}>
            <input
              type="radio"
              name="exportFormat"
              checked={format === "simple"}
              onChange={() => setFormat("simple")}
            />
            Simple
          </label>
          <label className={format === "v20" ? "active" : undefined}>
            <input
              type="radio"
              name="exportFormat"
              checked={format === "v20"}
              onChange={() => setFormat("v20")}
            />
            V20
          </label>
        </div>
      </div>
      <div className="form-group">
        <span className="form-label">Include</span>
        <div className="exportScopeToggle" role="radiogroup" aria-label="Export scope">
          <label className={completedOnly ? "active" : undefined}>
            <input
              type="radio"
              name="exportScope"
              checked={completedOnly}
              onChange={() => setCompletedOnly(true)}
            />
            Completed tests
          </label>
          <label className={!completedOnly ? "active" : undefined}>
            <input
              type="radio"
              name="exportScope"
              checked={!completedOnly}
              onChange={() => setCompletedOnly(false)}
            />
            All tests
          </label>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="downloadDate" className="form-label">
          Start date
        </label>
        <DateTimePicker
          id="downloadDate"
          field="downloadDate"
          pickerTitle="Start Date"
          value={selectedDate}
          onChange={handleDateChange}
          className="form-control"
          name="downloadDate"
          dateOnly
        />
      </div>
      <div className="form-group">
        <label htmlFor="downloadEndDate" className="form-label">
          End date
        </label>
        <DateTimePicker
          id="downloadEndDate"
          field="downloadEndDate"
          pickerTitle="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          className="form-control"
          name="downloadEndDate"
          dateOnly
        />
      </div>
      <p className="help-text">
        {completedOnly
          ? "Export finished questionnaires with a finish date on or after the start date and on or before the end date."
          : "Export all questionnaires created on or after the start date and on or before the end date, including those still in progress."}
      </p>
    </div>
  );

  const footer = (
    <div className="footerRight">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
      >
        <MdClose className="downloadDataModalButtonIcon" />
        Close
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleDownload}
        disabled={rangeInvalid}
      >
        <MdDownload className="downloadDataModalButtonIcon" />
        Download
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle}
      onClose={onClose}
      footer={footer}
      className="downloadDataModal"
      size="sm"
    >
      {bodyContent}
    </Modal>
  );
}
